import { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0B1B33";
const KEY = "splash_shown";
const TOTAL = 4700;
const GLYPHS = "0123456789ABCDEF01";

const wanted = () => {
  try {
    if (sessionStorage.getItem(KEY)) return false;
    return window.matchMedia("(max-width: 767px)").matches || window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
  } catch (_) { return false; }
};

/** Data-matrix rain that converges into the centre, drawn on one canvas (cheap, 60 fps). */
const useMatrix = (ref, reduced) => {
  useEffect(() => {
    if (reduced) return undefined;
    const c = ref.current;
    const g = c.getContext("2d");
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = window.innerWidth, H = window.innerHeight;
    c.width = W * dpr; c.height = H * dpr; g.scale(dpr, dpr);
    const fs = 14, cols = Math.ceil(W / fs);
    const drops = Array.from({ length: cols }, () => Math.random() * -H / fs);
    const speed = Array.from({ length: cols }, () => 0.35 + Math.random() * 0.6);
    const cx = W / 2, cy = H / 2 - 20;
    const parts = Array.from({ length: 70 }, () => { const a = Math.random() * Math.PI * 2, r = Math.hypot(W, H) * (0.35 + Math.random() * 0.25); return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, d: Math.random() * 0.2 }; });
    g.font = `600 ${fs - 2}px ui-monospace, Menlo, monospace`;
    let raf, start;
    const frame = (now) => {
      start ??= now;
      const t = (now - start) / 1000;
      g.fillStyle = "rgba(11,27,51,0.22)";
      g.fillRect(0, 0, W, H);
      const fade = Math.max(0.1, 1 - Math.max(0, t - 1.0) / 0.8);
      for (let i = 0; i < cols; i++) {
        const y = drops[i] * fs;
        g.fillStyle = `rgba(94,234,212,${0.85 * fade})`;
        g.fillText(GLYPHS[(Math.random() * GLYPHS.length) | 0], i * fs, y);
        g.fillStyle = `rgba(56,189,248,${0.35 * fade})`;
        g.fillText(GLYPHS[(Math.random() * GLYPHS.length) | 0], i * fs, y - fs);
        drops[i] = y > H && Math.random() > 0.96 ? 0 : drops[i] + speed[i] * (1 + t);
      }
      if (t > 0.9) {
        for (const p of parts) {
          const k = Math.min(1, Math.max(0, (t - 0.9 - p.d) / 0.6)), e = k * k * (3 - 2 * k);
          const x = p.x + (cx - p.x) * e, y = p.y + (cy - p.y) * e;
          g.fillStyle = `rgba(125,211,252,${0.9 * (1 - e * 0.7)})`;
          g.beginPath(); g.arc(x, y, 1.6 + (1 - e) * 1.4, 0, Math.PI * 2); g.fill();
        }
      }
      if (t < TOTAL / 1000) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [ref, reduced]);
};

/** Mobile / installed-app start-up splash (once per session): matrix → current app icon → fade into the app. Tap to skip. */
export const Splash = () => {
  const [show, setShow] = useState(wanted);
  const [leaving, setLeaving] = useState(false);
  const [icon, setIcon] = useState(null);
  const canvas = useRef(null);
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  useMatrix(canvas, reduced || !show);

  useEffect(() => {
    if (!show) return undefined;
    sessionStorage.setItem(KEY, "1");
    fetch(`${API}/api/brand/app-icon-meta`).then((r) => r.json()).then((m) => setIcon(`${API}/api/brand/app-icon/icon-512.png?v=${m.version}`))
      .catch(() => setIcon("/brand/icon-512.png?v=brain1"));
    const total = reduced ? 3000 : TOTAL;
    const a = setTimeout(() => setLeaving(true), total - 450);
    const b = setTimeout(() => setShow(false), total);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [show, reduced]);
  useEffect(() => { if (!show) { document.documentElement.style.background = ""; window.dispatchEvent(new Event("splash:done")); } }, [show]);

  if (!show) return null;
  const skip = () => { setLeaving(true); setTimeout(() => setShow(false), 250); };
  return (
    <div role="presentation" onClick={skip} data-testid="splash"
      className="fixed inset-0 z-[200] flex select-none items-center justify-center overflow-hidden"
      style={{ background: NAVY, opacity: leaving ? 0 : 1, transition: "opacity 450ms ease" }}>
      {!reduced && <canvas ref={canvas} className="absolute inset-0 h-full w-full" aria-hidden="true" />}
      <div className="relative flex flex-col items-center gap-3 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
        <div className="absolute left-1/2 top-[56px] h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-400/25 blur-3xl" style={{ animation: reduced ? "none" : "splash-glow 1s ease-out 1.25s both, splash-breathe 1.1s ease-in-out 2.25s infinite alternate" }} />
        {icon && <img src={icon} alt="" width="112" height="112" className="relative h-28 w-28 object-contain drop-shadow-[0_0_24px_rgba(56,189,248,0.55)]"
          style={{ animation: reduced ? "splash-fade 500ms ease both" : "splash-in 800ms cubic-bezier(.2,.9,.3,1.2) 1.3s both" }} data-testid="splash-icon" />}
        <span className="relative text-xs font-semibold tracking-[0.3em] text-cyan-100/80" style={{ animation: reduced ? "splash-fade 500ms ease both" : "splash-rise 600ms ease-out 1.8s both" }}>THE BRAIN</span>
      </div>
      <span className="absolute bottom-[max(1.25rem,env(safe-area-inset-bottom))] text-[10px] text-slate-500">Tap to skip</span>
    </div>
  );
};
